import { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, CardHeader, Typography, Button,
  Switch, FormControlLabel, Grid, Divider, FormControl,
  InputLabel, Select, MenuItem, TextField, Chip, Alert,
} from '@mui/material';
import toast from 'react-hot-toast';
import { attendanceApi } from '@/api/attendance.api';
import useSchoolStore from '@/store/schoolStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { PATHS } from '@/router/paths';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  Save, RefreshCw, UserCheck, ClipboardList, Clock,
  Bell, ShieldCheck, CalendarCheck2,
} from 'lucide-react';

// ── Default state matching the API response shape ────────────────
const DEFAULT_SETTINGS = {
  attendance_mode: 'DAILY',
  allow_edit: true,
  edit_allowed_days: 2,
  lock_after_submit: false,
  default_status: 'PRESENT',
  allow_future_attendance: false,
  allow_previous_attendance: true,
  notify_parents: false,
};

// ── Small helper: section label ───────────────────────────────────
function SectionLabel({ children }) {
  return (
    <Typography
      variant="subtitle2"
      color="text.secondary"
      fontWeight={700}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}
    >
      {children}
    </Typography>
  );
}

// ── Toggle row ───────────────────────────────────────────────────
function SettingToggle({ icon: Icon, label, description, checked, onChange, color = 'primary' }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {Icon && (
          <Box sx={{ mt: 0.3, color: `${color}.main` }}>
            <Icon size={18} />
          </Box>
        )}
        <Box>
          <Typography variant="body2" fontWeight={600}>{label}</Typography>
          {description && (
            <Typography variant="caption" color="text.secondary">{description}</Typography>
          )}
        </Box>
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={!!checked}
            onChange={(e) => onChange(e.target.checked)}
            color={color}
            size="small"
          />
        }
        label=""
        sx={{ m: 0 }}
      />
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────
export function StudentAttendanceSettingsPage() {
  usePageMeta('Student Attendance Settings | Profile', [
    { label: 'Profile', path: '/my-profile' },
    { label: 'Student Attendance Settings' },
  ]);

  const { currentSchool } = useSchoolStore();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!currentSchool?.id) return;
    try {
      setFetching(true);
      const data = await attendanceApi.getStudentAttendanceSettings(currentSchool.id);
      if (data?.settings) {
        setSettings(data.settings);
      } else if (data) {
        setSettings(data);
      }
    } catch (error) {
      toast.error('Failed to load student attendance settings');
      console.error(error);
    } finally {
      setFetching(false);
      setDirty(false);
    }
  }, [currentSchool]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const patch = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await attendanceApi.updateStudentAttendanceSettings(currentSchool.id, settings);
      toast.success('Student attendance settings updated successfully.');
      setDirty(false);
    } catch (error) {
      toast.error('Failed to update settings. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    fetchSettings();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Student Attendance Settings"
        subtitle="Configure how student attendance is recorded, edited, and communicated to parents"
        backPath={PATHS.SETTINGS}
        action={
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={handleReset}
              disabled={loading || fetching}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<Save size={16} />}
              onClick={handleSave}
              disabled={loading || fetching || !dirty}
            >
              {loading ? 'Saving…' : 'Save Settings'}
            </Button>
          </Box>
        }
      />

      {dirty && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          You have unsaved changes. Click <strong>Save Settings</strong> to apply them.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ── Left column: Core Rules ─────────────────────────────── */}
        <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ClipboardList size={20} />
                  <Typography variant="h6" fontWeight={600}>Core Attendance Rules</Typography>
                </Box>
              }
              subheader="Define how attendance is captured and the default mark applied"
            />
            <Divider />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3, flex: 1 }}>

              {/* Attendance Mode */}
              <Box>
                <SectionLabel>Recording Mode</SectionLabel>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Determines whether attendance is taken once per day or per class period
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Attendance Mode</InputLabel>
                      <Select
                        label="Attendance Mode"
                        value={settings.attendance_mode}
                        onChange={(e) => patch('attendance_mode', e.target.value)}
                      >
                        <MenuItem value="DAILY">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarCheck2 size={15} />
                            Daily (Once per day)
                          </Box>
                        </MenuItem>
                        <MenuItem value="PERIOD">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Clock size={15} />
                            Period-wise (Per class)
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Default Status</InputLabel>
                      <Select
                        label="Default Status"
                        value={settings.default_status}
                        onChange={(e) => patch('default_status', e.target.value)}
                      >
                        <MenuItem value="PRESENT">
                          <Chip label="Present" size="small" color="success" sx={{ fontSize: 11 }} />
                        </MenuItem>
                        <MenuItem value="ABSENT">
                          <Chip label="Absent" size="small" color="error" sx={{ fontSize: 11 }} />
                        </MenuItem>
                        <MenuItem value="UNMARKED">
                          <Chip label="Unmarked" size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Edit Rules */}
              <Box>
                <SectionLabel>Edit Rules</SectionLabel>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Control who can edit attendance after it has been submitted
                </Typography>
                <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!settings.allow_edit}
                          onChange={(e) => patch('allow_edit', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Allow Editing</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Teachers can correct submitted records
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Editable for (Days)"
                      value={settings.edit_allowed_days ?? 2}
                      disabled={!settings.allow_edit}
                      onChange={(e) => patch('edit_allowed_days', Number(e.target.value))}
                      inputProps={{ min: 0, max: 30 }}
                      helperText="Max days past the attendance date"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Date Range */}
              <Box>
                <SectionLabel>Date Range Permissions</SectionLabel>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Restrict which dates teachers can record attendance for
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <SettingToggle
                      icon={CalendarCheck2}
                      label="Allow Previous Dates"
                      description="Teachers can mark past attendance"
                      checked={settings.allow_previous_attendance}
                      onChange={(v) => patch('allow_previous_attendance', v)}
                      color="primary"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <SettingToggle
                      icon={Clock}
                      label="Allow Future Dates"
                      description="Teachers can mark upcoming dates"
                      checked={settings.allow_future_attendance}
                      onChange={(v) => patch('allow_future_attendance', v)}
                      color="warning"
                    />
                  </Grid>
                </Grid>
              </Box>

            </CardContent>
          </Card>
        </Grid>

        {/* ── Right column: Submission & Notifications ─────────────── */}
        <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ShieldCheck size={20} />
                  <Typography variant="h6" fontWeight={600}>Submission & Alerts</Typography>
                </Box>
              }
              subheader="Control locking behaviour and parent notification preferences"
            />
            <Divider />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 3, flex: 1 }}>

              <Box>
                <SectionLabel>Submission Lock</SectionLabel>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Prevent changes after attendance has been submitted for the day
                </Typography>
                <SettingToggle
                  icon={UserCheck}
                  label="Lock After Submit"
                  description="Once submitted, attendance cannot be changed without admin override"
                  checked={settings.lock_after_submit}
                  onChange={(v) => patch('lock_after_submit', v)}
                  color="error"
                />
              </Box>

              <Divider />

              <Box>
                <SectionLabel>Parent Notifications</SectionLabel>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Automatically alert parents when their child is marked absent
                </Typography>
                <SettingToggle
                  icon={Bell}
                  label="Notify Parents on Absence"
                  description="Send SMS / app notification to parent when student is absent"
                  checked={settings.notify_parents}
                  onChange={(v) => patch('notify_parents', v)}
                  color="success"
                />
              </Box>

              <Divider />

              {/* Live summary */}
              <Box>
                <SectionLabel>Current Configuration</SectionLabel>
                <Box
                  sx={{
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {[
                    { label: 'Mode', value: settings.attendance_mode },
                    { label: 'Default Status', value: settings.default_status },
                    {
                      label: 'Edit Window',
                      value: settings.allow_edit
                        ? `${settings.edit_allowed_days} day(s)`
                        : 'Disabled',
                    },
                    { label: 'Lock on Submit', value: settings.lock_after_submit ? 'Yes' : 'No' },
                    { label: 'Past Dates', value: settings.allow_previous_attendance ? 'Allowed' : 'Blocked' },
                    { label: 'Future Dates', value: settings.allow_future_attendance ? 'Allowed' : 'Blocked' },
                    { label: 'Parent Alerts', value: settings.notify_parents ? 'Enabled' : 'Disabled' },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="caption" fontWeight={600}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
